import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Button,
  Chip,
  useTheme,
  TablePagination,
  Tooltip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Drawer,
  useMediaQuery,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Fingerprint as FingerprintIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import {
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import { getAllClients } from '../../services/firebase';
import { formatCurrency, formatDate } from '../../utils/formatters';
import ClientFilter, { FilterOptions } from '../../components/ClientFilter';
import { filterClients } from '../../utils/filters';
import { useAuth } from '../../contexts/AuthContext';
import { computeMetrics, OwnerMetrics } from '../../utils/staffMetrics';

type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'activeCases' | 'balanceOwed' | 'applicationDate';

interface ClientSummary {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  countryOfOrigin: string;
  activeCases: number;
  balanceOwed: number;
  applicationDate: Date | null;
  cases: Array<{
    biometricTaken: boolean;
    applicationDate: string;
  }>;
}

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || first.toUpperCase();
};

// Stable color for an avatar based on name hash
const avatarColorFor = (name: string): string => {
  const palette = ['#1E3A5F', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#14B8A6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  accent: string;
  accentBg: string;
  loading?: boolean;
}> = ({ label, value, sub, icon, accent, accentBg, loading }) => (
  <Paper
    sx={{
      flex: '1 1 220px',
      minWidth: { xs: '100%', sm: 200 },
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        bgcolor: accentBg,
        color: accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '1.375rem',
          fontWeight: 700,
          color: 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.25,
        }}
      >
        {loading ? '—' : value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, role, staffUser, refreshStaffUser } = useAuth();
  const ownerFilter = role === 'staff' ? currentUser?.uid : undefined;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [allClients, setAllClients] = useState<ClientSummary[]>([]);
  const [metrics, setMetrics] = useState<OwnerMetrics>({
    totalCharged: 0,
    totalCollected: 0,
    totalBalance: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('applicationDate');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: 'all',
    biometricStatus: 'all',
    archiveStatus: 'active',
    country: 'all',
    customStartDate: undefined,
    customEndDate: undefined,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionMenu, setActionMenu] = useState<{
    anchor: HTMLElement;
    clientId: string;
  } | null>(null);

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, ownerFilter]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllClients(false, ownerFilter);
      const summaries = calculateSummary(data);
      setAllClients(summaries);
      setClients(summaries);
      // Financial roll-up across the loaded book (own clients for staff,
      // all clients for admin/manager). Drives the summary cards.
      setMetrics(computeMetrics(data));
      // Pull the latest staff-pay total an admin may have recorded since the
      // auth listener last loaded the user doc, so the Staff Pay card is current.
      refreshStaffUser();
    } catch (err) {
      console.error('Dashboard: Error loading clients:', err);
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: FilterOptions) => {
    setFilters(newFilters);
    setPage(0);
  };

  useEffect(() => {
    if (allClients.length > 0) {
      const clientsForFiltering = allClients.map((summary) => ({
        id: summary.id,
        fullName: summary.name,
        email: summary.email,
        phoneNumber: summary.phoneNumber,
        countryOfOrigin: summary.countryOfOrigin,
        cases: summary.cases.map((c) => ({
          ...c,
          id: '',
          clientId: summary.id,
          type: 'Asylum' as const,
          receiptNumber: '',
          amountCharged: 0,
          status: 'Active' as const,
          workPermitEligible: false,
          payments: [],
          notes: [],
          documents: [],
          createdAt: { toDate: () => new Date() } as any,
          updatedAt: { toDate: () => new Date() } as any,
        })),
        createdAt: { toDate: () => new Date() } as any,
        updatedAt: { toDate: () => new Date() } as any,
      }));

      const filtered = filterClients(clientsForFiltering, filters);
      const filteredSummaries = allClients.filter((summary) =>
        filtered.some((f) => f.id === summary.id),
      );
      setClients(filteredSummaries);
    }
  }, [allClients, filters]);

  const calculateSummary = (data: any[]) => {
    return data.map((client) => {
      const sortedCases = [...client.cases].sort(
        (a, b) =>
          new Date(a.applicationDate).getTime() -
          new Date(b.applicationDate).getTime(),
      );
      const firstCase = sortedCases[0];
      return {
        id: client.id,
        name: client.fullName,
        phoneNumber: client.phoneNumber,
        email: client.email,
        countryOfOrigin: client.countryOfOrigin || '',
        activeCases: client.cases.filter((c: any) => c.status === 'Active').length,
        balanceOwed: client.cases.reduce((sum: number, c: any) => {
          const totalPaid = c.payments.reduce(
            (pSum: number, p: any) => pSum + (Number(p.amount) || 0),
            0,
          );
          return sum + (c.amountCharged - totalPaid);
        }, 0),
        applicationDate: firstCase ? new Date(firstCase.applicationDate) : null,
        cases: client.cases.map((c: any) => ({
          biometricTaken: c.biometricTaken,
          applicationDate: c.applicationDate,
        })),
      };
    });
  };

  const handleSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleRetry = () => {
    setError(null);
    setRefreshKey((p) => p + 1);
  };

  const filteredClients = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phoneNumber.includes(searchTerm),
  );

  const sortedClients = [...filteredClients].sort((a, b) => {
    const cmp = (x: ClientSummary, y: ClientSummary) => {
      if (orderBy === 'name') return x.name.localeCompare(y.name);
      if (orderBy === 'activeCases') return x.activeCases - y.activeCases;
      if (orderBy === 'balanceOwed') return x.balanceOwed - y.balanceOwed;
      if (orderBy === 'applicationDate') {
        if (!x.applicationDate && !y.applicationDate) return 0;
        if (!x.applicationDate) return 1;
        if (!y.applicationDate) return -1;
        return x.applicationDate!.getTime() - y.applicationDate!.getTime();
      }
      return 0;
    };
    return order === 'asc' ? cmp(a, b) : cmp(b, a);
  });

  const hasFingerprintsTaken = (client: ClientSummary) =>
    client.cases.some((c) => c.biometricTaken);

  const filterCount =
    (filters.dateRange !== 'all' ? 1 : 0) +
    (filters.biometricStatus !== 'all' ? 1 : 0) +
    (filters.archiveStatus !== 'active' ? 1 : 0);

  const pageRows = sortedClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const isStaff = role === 'staff';
  const staffPay = staffUser?.staffPayTotal ?? 0;
  const staffPayCount = staffUser?.staffPayments?.length ?? 0;

  if (error) {
    return (
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 4, sm: 6, md: 8 }, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Cell sx for sticky-first-column on mobile (left edge stays visible while horizontal scrolling).
  const stickyFirstHead = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 3,
    backgroundColor: '#F9FAFB',
  };
  const stickyFirstBody = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 1,
    backgroundColor: 'inherit',
  };

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        px: { xs: 4, sm: 6, md: 8 },
        py: { xs: 5, md: 6 },
      }}
    >
      {/* Page header */}
      <Box sx={{ mb: 6 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.3,
          }}
        >
          Dashboard
        </Typography>
      </Box>

      {/* Summary metrics */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 6 }}>
        <StatCard
          label="Total Collected"
          value={formatCurrency(metrics.totalCollected)}
          sub="Payments received to date"
          icon={<TrendingUpIcon />}
          accent="#059669"
          accentBg="#ECFDF5"
          loading={loading}
        />
        <StatCard
          label="Total Balance"
          value={formatCurrency(metrics.totalBalance)}
          sub="Outstanding across cases"
          icon={<WalletIcon />}
          accent="#B45309"
          accentBg="#FFFBEB"
          loading={loading}
        />
        {isStaff && (
          <StatCard
            label="Staff Pay"
            value={formatCurrency(staffPay)}
            sub={
              staffPayCount > 0
                ? `Paid to you across ${staffPayCount} ${
                    staffPayCount === 1 ? 'payment' : 'payments'
                  }`
                : 'No pay recorded yet'
            }
            icon={<PaymentsIcon />}
            accent="#1D4ED8"
            accentBg="#EFF6FF"
          />
        )}
      </Box>

      {/* Recent Clients section */}
      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: { xs: 4, sm: 5 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography
              component="h2"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Recent Clients
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
              {sortedClients.length} of {allClients.length} {allClients.length === 1 ? 'client' : 'clients'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <TextField
              size="small"
              placeholder="Search by name…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 280 } }}
              inputProps={{ 'aria-label': 'Search clients' }}
            />
            <Button
              startIcon={<FilterListIcon />}
              onClick={() => setFilterDrawerOpen(true)}
              variant="outlined"
              sx={{ flexShrink: 0 }}
            >
              Filters
              {filterCount > 0 && (
                <Chip
                  label={filterCount}
                  size="small"
                  color="primary"
                  sx={{
                    ml: 1,
                    height: 18,
                    minWidth: 18,
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                  }}
                />
              )}
            </Button>
            <Button
              startIcon={<PersonAddIcon />}
              onClick={() => navigate('/add-client')}
              variant="contained"
              color="primary"
              sx={{ flexShrink: 0 }}
            >
              Add Client
            </Button>
          </Box>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 760 }} aria-label="Recent clients">
            <TableHead>
              <TableRow>
                <TableCell sx={stickyFirstHead}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'activeCases'}
                    direction={orderBy === 'activeCases' ? order : 'asc'}
                    onClick={() => handleSort('activeCases')}
                  >
                    Active Cases
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'balanceOwed'}
                    direction={orderBy === 'balanceOwed' ? order : 'asc'}
                    onClick={() => handleSort('balanceOwed')}
                  >
                    Balance Owed
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'applicationDate'}
                    direction={orderBy === 'applicationDate' ? order : 'asc'}
                    onClick={() => handleSort('applicationDate')}
                  >
                    Application Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 140 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : sortedClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <Typography sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                      No clients found
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                      Try adjusting your search or filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((client, idx) => {
                  const fp = hasFingerprintsTaken(client);
                  const initials = initialsFromName(client.name);
                  const avatarColor = avatarColorFor(client.name);
                  // Per-row bg controls inheritance of sticky first column on desktop alt rows
                  const rowBg = idx % 2 === 1 ? '#F9FAFB' : '#FFFFFF';
                  return (
                    <TableRow
                      key={client.id}
                      hover
                      sx={{
                        backgroundColor: { xs: '#FFFFFF', md: rowBg },
                        '& td': { height: 56 },
                        '&:hover .row-actions': { opacity: 1 },
                        '@media (hover: none)': {
                          '& .row-actions': { opacity: 1 },
                        },
                      }}
                    >
                      <TableCell sx={stickyFirstBody}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            invisible={!fp}
                            badgeContent={
                              <Box
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  bgcolor: '#10B981',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '2px solid #FFFFFF',
                                }}
                                aria-label="Fingerprints taken"
                              >
                                <FingerprintIcon sx={{ fontSize: 8, color: '#FFFFFF' }} />
                              </Box>
                            }
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: avatarColor,
                                fontSize: '0.75rem',
                                color: '#FFFFFF',
                              }}
                            >
                              {initials}
                            </Avatar>
                          </Badge>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: 'text.primary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: { xs: 140, sm: 220, md: 280 },
                            }}
                          >
                            {client.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${client.activeCases} ${client.activeCases === 1 ? 'case' : 'cases'}`}
                          size="small"
                          sx={{
                            bgcolor: client.activeCases > 0 ? '#EFF6FF' : '#F3F4F6',
                            color: client.activeCases > 0 ? '#1D4ED8' : '#6B7280',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          fontWeight: 500,
                          color: client.balanceOwed > 0 ? 'text.primary' : 'text.secondary',
                        }}
                      >
                        {formatCurrency(client.balanceOwed)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {client.applicationDate ? formatDate(client.applicationDate) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          className="row-actions"
                          sx={{
                            display: 'inline-flex',
                            gap: 0.25,
                            opacity: { xs: 1, md: 0 },
                            transition: 'opacity 150ms ease',
                          }}
                        >
                          <Tooltip title="View" arrow>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/client/${client.id}`)}
                              aria-label={`View ${client.name}`}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/client/${client.id}`)}
                              aria-label={`Edit ${client.name}`}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More actions" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                setActionMenu({ anchor: e.currentTarget, clientId: client.id })
                              }
                              aria-label={`More actions for ${client.name}`}
                            >
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={sortedClients.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows:"
          labelDisplayedRows={({ from, to, count }) =>
            `Showing ${count === 0 ? 0 : from}–${to} of ${count} ${count === 1 ? 'client' : 'clients'}`
          }
        />
      </Paper>

      {/* Row action menu */}
      <Menu
        anchorEl={actionMenu?.anchor ?? null}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: 180,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionMenu) navigate(`/client/${actionMenu.clientId}`);
            setActionMenu(null);
          }}
          sx={{ fontSize: '0.875rem', gap: 1.5 }}
        >
          <VisibilityIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          View Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenu) navigate(`/client/${actionMenu.clientId}`);
            setActionMenu(null);
          }}
          sx={{ fontSize: '0.875rem', gap: 1.5 }}
        >
          <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Edit Details
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => setActionMenu(null)}
          sx={{ fontSize: '0.875rem', gap: 1.5, color: 'text.secondary' }}
        >
          <ArchiveIcon sx={{ fontSize: 18 }} />
          Archive Client
        </MenuItem>
      </Menu>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: isXs ? '100%' : 400,
            p: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            boxShadow: '-8px 0 24px rgba(0,0,0,0.06)',
          },
        }}
      >
        <ClientFilter
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClose={() => setFilterDrawerOpen(false)}
          onApply={() => setFilterDrawerOpen(false)}
          showActiveFilters
          totalCount={allClients.length}
          visibleCount={clients.length}
          countryOptions={Array.from(
            new Set(allClients.map((c) => c.countryOfOrigin).filter(Boolean)),
          )}
        />
      </Drawer>
    </Box>
  );
};

export default Dashboard;
